import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Scroll3DSection, Tilt3D } from './Scroll3D';
import {
  Building2,
  Globe,
  Zap,
  Star,
  ArrowRight,
  TrendingUp,
  Users,
  Award,
  Code,
  Smartphone,
  Shield,
  Database
} from 'lucide-react';

// Import images from sub folder
import tareen from '../data/sub/tareen.png';
import pl from '../data/sub/pl.png';
import moraAi from '../data/sub/mora-ai.png';
import endak from '../data/sub/endak.png';
import antyeek from '../data/sub/antyeek.png';

const Subsidiaries = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const subsidiaries = [
    {
      name: t('subsidiaries.tareen.name'),
      description: t('subsidiaries.tareen.description'),
      image: tareen,
      industry: t('subsidiaries.tareen.industry'),
      founded: '2020',
      link: 'http://tareen.app/',
      focus: t('subsidiaries.tareen.focus'),
    },
    {
      name: t('subsidiaries.endak.name'),
      description: t('subsidiaries.endak.description'),
      image: endak,
      industry: t('subsidiaries.endak.industry'),
      founded: '2021',
      link: 'https://endak.app',
      focus: t('subsidiaries.endak.focus'),
    },
    {
      name: t('subsidiaries.pl.name'),
      description: t('subsidiaries.pl.description'),
      image: pl,
      industry: t('subsidiaries.pl.industry'),
      founded: '2019',
      link: 'https://pl-ksa.org',
      focus: t('subsidiaries.pl.focus'),
    },
    {
      name: t('subsidiaries.moraAi.name'),
      description: t('subsidiaries.moraAi.description'),
      image: moraAi,
      industry: t('subsidiaries.moraAi.industry'),
      founded: '2022',
      link: 'https://ai-mora.com/',
      focus: t('subsidiaries.moraAi.focus'),
    },
    {
      name: t('subsidiaries.antyeek.name'),
      description: t('subsidiaries.antyeek.description'),
      image: antyeek,
      industry: t('subsidiaries.antyeek.industry'),
      founded: '2021',
      link: 'https://antyeek.com',
      focus: t('subsidiaries.antyeek.focus'),
    },
  ];

  const serviceIcons = [
    { icon: Code, color: 'from-neon-green to-neon-cyan' },
    { icon: Smartphone, color: 'from-neon-cyan to-neon-blue' },
    { icon: Globe, color: 'from-neon-blue to-neon-purple' },
    { icon: Shield, color: 'from-neon-purple to-neon-green' },
    { icon: Database, color: 'from-neon-green to-neon-cyan' },
  ];

  return (
    <section id="subsidiaries" ref={ref} className="py-10 bg-transparent dark:bg-transparent relative overflow-hidden">
      {/* Animated Neon Background */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[80vw] h-48 bg-gradient-to-r from-neon-green/20 via-neon-cyan/10 to-neon-purple/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60vw] h-32 bg-gradient-to-r from-neon-cyan/10 via-neon-green/10 to-neon-purple/10 rounded-full blur-2xl animate-pulse-slower"></div>
      </div>

      <Scroll3DSection className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Enhanced Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center space-x-3 rtl:space-x-reverse bg-gradient-to-r from-neon-green/30 to-neon-cyan/30 backdrop-blur-xl border border-neon-green/40 rounded-full px-10 py-5 mb-8 shadow-2xl shadow-neon-green/30 animate-glow"
          >
            <Building2 className="w-8 h-8 text-neon-green animate-spin-slow" />
            <span className="text-neon-green font-black text-2xl tracking-wide uppercase drop-shadow-lg">{t('subsidiaries.badge')}</span>
            <Star className="w-6 h-6 text-neon-cyan animate-pulse" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-6xl sm:text-7xl lg:text-8xl font-black text-white mb-8 tracking-tight drop-shadow-neon"
          >
            <span className="gradient-text-neon drop-shadow-2xl">{t('subsidiaries.title')}</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-2xl sm:text-3xl text-white/80 max-w-4xl mx-auto leading-relaxed font-light drop-shadow-lg"
          >
            {t('subsidiaries.subtitle')}
          </motion.p>
        </motion.div>

        {/* Stunning Subsidiaries Carousel */}
        <div className="relative z-10 pt-16">
          <div className="flex items-center justify-center">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 lg:gap-8 w-full max-w-7xl mx-auto"
              style={{ perspective: '1200px' }}
            >
              {subsidiaries.map((subsidiary, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="w-full h-full"
                >
                  <Tilt3D className="w-full h-full">
                    <div className="group relative bg-gradient-to-br from-black/90 via-black/70 to-black/90 backdrop-blur-xl border border-neon-green/40 rounded-3xl p-6 lg:p-8 hover:border-neon-green/70 hover:shadow-2xl hover:shadow-neon-green/30 transition-all duration-500 card-hover-neon flex flex-col h-[44rem] w-full overflow-hidden">
                      {/* Enhanced Animated Background Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-neon-green/10 via-transparent to-neon-cyan/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                      {/* Enhanced Glowing Border Effect */}
                      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-neon-green/30 via-neon-cyan/20 to-neon-purple/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>

                      {/* Logo Section with Enhanced Design */}
                      <div className="relative z-10 mb-6">
                        <div className="relative w-28 h-28 mx-auto flex items-center justify-center">
                          {/* Outer Glow Ring */}
                          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-neon-green/50 via-neon-cyan/40 to-neon-purple/50 blur-xl opacity-80 animate-pulse-slow"></div>

                          {/* Middle Ring */}
                          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-neon-green/30 to-neon-cyan/30 border-2 border-neon-green/50 animate-spin-slow"></div>

                          {/* Logo Container */}
                          <div className="relative w-24 h-24 rounded-2xl bg-gradient-to-br from-white/95 to-white/90 border-2 border-neon-green/50 flex items-center justify-center shadow-2xl shadow-neon-green/30 group-hover:shadow-neon-green/50 transition-all duration-500">
                            <img
                              src={subsidiary.image}
                              alt={`${subsidiary.name} - ${subsidiary.industry} Subsidiary of Saham Holding`}
                              className="w-20 h-20 object-contain rounded-xl drop-shadow-lg group-hover:scale-110 transition-transform duration-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="relative z-10 flex-1 flex flex-col justify-between">
                        {/* Name & Industry */}
                        <div className="text-center mb-6">
                          <h3 className="text-2xl font-black text-white mb-2 group-hover:text-neon-green transition-colors duration-300 drop-shadow-lg">
                            {subsidiary.name}
                          </h3>
                          <div className="inline-block bg-gradient-to-r from-neon-cyan/15 to-neon-blue/15 backdrop-blur-sm border border-neon-cyan/25 rounded-full px-2 py-1 shadow-sm">
                            <span className="text-neon-cyan text-xs font-medium tracking-wide uppercase">
                              {subsidiary.industry}
                            </span>
                          </div>
                        </div>

                        {/* Description - Shorter */}
                        <div className="mb-6 h-16 flex items-center">
                          <p className="text-white/85 leading-relaxed text-sm text-center px-2 group-hover:text-white/95 transition-colors duration-300 w-full line-clamp-2">
                            {subsidiary.description}
                          </p>
                        </div>

                        {/* Enhanced Info Panel */}
                        <div className="mb-6">
                          <div className="rounded-2xl bg-gradient-to-r from-white/15 to-white/8 backdrop-blur-md border border-neon-green/30 p-4 shadow-inner group-hover:border-neon-green/50 transition-all duration-300">
                            <div className="flex justify-between items-center mb-3">
                              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                <div className="w-2 h-2 bg-neon-green rounded-full animate-pulse shadow-lg shadow-neon-green/50"></div>
                                <span className="text-xs text-white/80 font-semibold">Founded</span>
                              </div>
                              <span className="font-bold text-neon-cyan text-sm">{subsidiary.founded}</span>
                            </div>

                            {/* Focus Area - Shorter */}
                            <div className="pt-3 border-t border-white/20">
                              <div className="text-xs text-white/70 mb-2 font-semibold">Focus</div>
                              <div className="text-xs text-white/95 font-medium leading-tight line-clamp-2">
                                {subsidiary.focus}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Enhanced CTA Button */}
                        <motion.a
                          href={subsidiary.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          className="relative w-full bg-gradient-to-r from-neon-green to-neon-cyan text-black px-4 py-2 rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-neon-green/40 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse group/btn overflow-hidden"
                        >
                          {/* Button Background Animation */}
                          <div className="absolute inset-0 bg-gradient-to-r from-neon-cyan to-neon-green opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>

                          <span className="relative z-10">{t('subsidiaries.learnMore')}</span>
                          <ArrowRight className="relative z-10 w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                        </motion.a>
                      </div>

                      {/* Enhanced Floating Tech Icons */}
                      <div className="absolute top-4 right-4 opacity-30 group-hover:opacity-60 transition-opacity duration-500">
                        <Code className="w-6 h-6 text-neon-green animate-bounce" style={{ animationDelay: '0s' }} />
                      </div>
                      <div className="absolute bottom-24 left-4 opacity-30 group-hover:opacity-60 transition-opacity duration-500">
                        <Globe className="w-5 h-5 text-neon-cyan animate-bounce" style={{ animationDelay: '0.5s' }} />
                      </div>
                    </div>
                  </Tilt3D>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Enhanced Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { number: '5', label: t('subsidiaries.stats.subsidiaries'), icon: Building2 },
              { number: '500+', label: t('subsidiaries.stats.employees'), icon: Users },
              { number: '4', label: t('subsidiaries.stats.years'), icon: Award },
              { number: '100%', label: t('subsidiaries.stats.success'), icon: TrendingUp },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-8 h-8 text-neon-green" />
                </div>
                <div className="text-4xl font-black text-white mb-2 gradient-text-neon">
                  {stat.number}
                </div>
                <div className="text-white/70 font-medium text-sm">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Scroll3DSection>
    </section>
  );
};

export default Subsidiaries; 